import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';

import {
  useWindowStore, useConfigStore, useMapStateStore, useMapDataStore,
} from '../../stores';
import SiteSeeContext from '../SiteSeeGuides/SiteSeeContext';
import { getPulseTarget, PULSE_TARGETS } from '../SiteSeeGuides/SiteSeeGuideData';

import ResidenceFilterContent from './ResidenceFilterContent';
import SiteSeeTipTrigger from '../SiteSeeTipTrigger/SiteSeeTipTrigger';
import { ReactComponent as FiltersIcon } from '../../SVGAssets/filters-icon.svg';
import { ReactComponent as ArrowUp } from '../../SVGAssets/arrow-up.svg';
import OnImageDropdown from '../OnImageDropdown/OnImageDropdown';

import '../../GlobalScss/common.scss';
import '../OnImageDropdown/OnImageDropdown.scss';
import './ResidenceFilter.scss';

const ResidenceFilter = ( { showByFieldName } ) => {
  const [ externalToggleActive, setExternalToggleActive ] = useState( false );
  const [ filtersVisible, setFiltersVisible ] = useState( false );

  const isMountedRef = useRef( true );

  // Zustand stores
  const isMobile = useWindowStore((state) => state.isMobile);
  const mapConfig = useConfigStore((state) => state.mapConfig);
  const activeZoom = useMapStateStore((state) => state.activeZoom);
  const activeMapType = useMapStateStore((state) => state.activeMapType);
  const POIVisible = useMapStateStore((state) => state.POIVisible);
  const svgFocusId = useMapStateStore((state) => state.svgFocusId);
  const mapFilterData = useMapDataStore((state) => state.mapFilterData);
  const { googleMapsConfig } = mapConfig || {};
  const { enableLevels: mapZoomToEnableLevels } = googleMapsConfig?.zooms || {};

  // SiteSee context for guide state
  const siteSeeContext = useContext(SiteSeeContext);
  const { siteSeeGuideId, siteSeeGuideMode } = siteSeeContext || {};

  // Determine if filters should pulse based on active guide
  const isCoached = siteSeeGuideMode === 'tour';
  const pulseTarget = getPulseTarget(siteSeeGuideId, isMobile);
  const isPulsing = isCoached && pulseTarget === PULSE_TARGETS.FILTERS;

  // svgFocusId carries both residence ids and place ids. Only a focused
  // residence should hide the filters: a place focus (an amenity picked from
  // the TourGuide AI chat) has no "Show All Residences" reset to clear it, so
  // treating it as a residence left the widget hidden for the rest of the
  // session. Until the residence data has loaded we cannot tell the two apart,
  // so keep hiding on any focus rather than letting the widget flash in.
  const focusIsResidence = useMemo( () => {
    if ( !svgFocusId ) return false;
    if ( !Array.isArray( mapFilterData ) || !mapFilterData.length ) return true;
    return mapFilterData.some( ( unit ) => unit?.svg_id === svgFocusId );
  }, [svgFocusId, mapFilterData] );

  // Cleanup mounted ref on unmount
  useEffect( () => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, [] );

  useEffect( () => {
    if ( !isMountedRef.current ) return;

    if (
      mapZoomToEnableLevels
      && activeZoom
      && activeMapType === 'roadmap'
      && !POIVisible
    ) {
      const rawControlVisible = activeZoom >= mapZoomToEnableLevels;
      if ( isMountedRef.current ) {
        setFiltersVisible( rawControlVisible );
      }
    } else if ( isMountedRef.current ) {
      setFiltersVisible( false );
    }
    // Don't show the filter widget if a residence is focused.
    if ( focusIsResidence ) {
      setFiltersVisible( false );
    }
  }, [
    mapZoomToEnableLevels,
    activeZoom,
    activeMapType,
    POIVisible,
    focusIsResidence,
  ] );

  // If we are on the Floor Plans page, make the filter visible IMMEDIATELY
  const { pathname } = useLocation();

  // Set visibility immediately on floor plans page (not in useEffect)
  // This ensures the "Loading..." message shows right away
  const isFloorPlansPage = pathname.includes( 'floor-plans' );

  useEffect( () => {
    if ( isFloorPlansPage && !filtersVisible ) {
      setFiltersVisible( true );
    }
  }, [pathname, isFloorPlansPage, filtersVisible]);

  return (
    <>

      { isMobile && (
        <button
          id="residenceFilterToggle"
          type="button"
          className={classNames( 'mobileMapControl', {
            'mobileMapControl--disabled': !filtersVisible,
            'mobileMapControl--pulsing': isPulsing,
          } )}
          onClick={() => setExternalToggleActive( !externalToggleActive )}
        >
          <div className="sr-only">Filter Residences</div>
          <FiltersIcon className="mobileMapControl__svg" />
        </button>
      )}

      <div className={classNames( {
        mapResidenceFilter__wrapper: !isMobile,
        'mapResidenceFilter__wrapper--hide': !isMobile && !filtersVisible,
        'mapResidenceFilter__wrapper--pulsing': !isMobile && isPulsing,
      } )}
      >

        <OnImageDropdown
          dropdownId="residenceFilter"
          className={classNames( {
            residenceFilter: !isMobile,
            mobileResidenceFilter: isMobile,
          } )}
          title="Filters"
          optionsLocation="bottom"
          closeDropdownOnClick
          useExternalToggle={isMobile}
          externalToggleActive={externalToggleActive}
          setExternalToggleActive={setExternalToggleActive}
          altToggleSvg={<ArrowUp />}
        >
          <ResidenceFilterContent
            showByFieldName={showByFieldName}
          />
        </OnImageDropdown>

        <SiteSeeTipTrigger siteSeeTipId="filters" />

      </div>
    </>
  );
};

export default ResidenceFilter;
